class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = new Map();
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.form = this.querySelector('form.search');
    this.abortController = null;
    this.isOpen = false;

    if (this.input && this.predictiveSearchResults && this.form) {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', debounce(() => this.onChange(), 250));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getQueryKey(searchTerm) {
    return searchTerm.toLocaleLowerCase().replace(/\s+/g, ' ');
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length < 2) {
      this.cancelPendingRequest();
      this.close(false);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    const selectedResult = this.querySelector('[role="option"][aria-selected="true"] a');

    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }

    if (selectedResult) {
      event.preventDefault();
      selectedResult.click();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();
    if (searchTerm.length < 2) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    window.setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close(false);
    }, 150);
  }

  onKeyup(event) {
    if (event.code === 'ArrowUp') {
      this.switchOption('up');
    } else if (event.code === 'ArrowDown') {
      this.switchOption('down');
    } else if (event.code === 'Enter' && this.isOpen) {
      this.selectOption();
    } else if (event.code === 'Escape') {
      this.close(false);
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.isOpen) return;

    const options = Array.from(this.querySelectorAll('[role="option"]'));
    if (!options.length) return;

    const selectedElement = this.querySelector('[role="option"][aria-selected="true"]');
    const selectedIndex = options.indexOf(selectedElement);
    let nextIndex;

    if (direction === 'up') {
      nextIndex = selectedIndex <= 0 ? options.length - 1 : selectedIndex - 1;
    } else {
      nextIndex = selectedIndex >= options.length - 1 ? 0 : selectedIndex + 1;
    }

    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');

    const activeElement = options[nextIndex];
    activeElement.setAttribute('aria-selected', 'true');
    activeElement.scrollIntoView({ block: 'nearest' });
    this.input.setAttribute('aria-activedescendant', activeElement.id);
    this.setLiveRegionText(activeElement.textContent.trim());
  }

  selectOption() {
    const selectedResult = this.querySelector('[role="option"][aria-selected="true"] a, [role="option"][aria-selected="true"] button');
    if (selectedResult) selectedResult.click();
  }

  cancelPendingRequest() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = this.getQueryKey(searchTerm);
    this.setLiveRegionLoadingState();

    if (this.cachedResults.has(queryKey)) {
      this.renderSearchResults(this.cachedResults.get(queryKey));
      return;
    }

    this.cancelPendingRequest();
    this.abortController = new AbortController();

    const query = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search'
    });

    fetch(`${routes.predictive_search_url}?${query.toString()}`, {
      signal: this.abortController.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const section = documentFragment.querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search response is missing its section');

        const resultsMarkup = section.innerHTML;
        this.cachedResults.set(queryKey, resultsMarkup);

        if (this.cachedResults.size > 20) {
          this.cachedResults.delete(this.cachedResults.keys().next().value);
        }

        if (this.getQueryKey(this.getQuery()) === queryKey) {
          this.renderSearchResults(resultsMarkup);
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          this.close(false);
          this.setLiveRegionText('La ricerca non e momentaneamente disponibile. Riprova.');
        }
      })
      .finally(() => {
        this.abortController = null;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text') || 'Ricerca in corso';
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    window.setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');
    this.removeAttribute('loading');

    const resultCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (resultCount) this.setLiveRegionText(resultCount.textContent.trim());

    this.open();
  }

  getResultsMaxHeight() {
    const header = document.getElementById('shopify-section-header');
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    return Math.max(240, window.innerHeight - headerBottom - 16);
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) this.input.value = '';

    const selected = this.querySelector('[role="option"][aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');

    this.input.removeAttribute('aria-activedescendant');
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
